<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import {
  actorTypeSelectOptions,
  diff,
  type Actor,
  type OpenMode,
} from "@/types";
import { actor, type ActorCreation } from "@/types";
import { pictogram } from "@/types";
import { computed, ref, inject, watch } from "vue";
import { MDBTextarea } from "mdb-vue-ui-kit";
import FormInput from "../FormInput.vue";
import { useRouter } from "vue-router";
import { actorsPathName } from "@/router";
import { mutateActorKey } from "@/logic/provide";
import FormSelect from "../FormSelect.vue";

const router = useRouter();
const props = defineProps<{
  modelValue: Actor;
  openMode: OpenMode;
  minified?: boolean;
}>();
const editActor = inject(mutateActorKey);

const minified = ref(props.minified || false);
const editMode = ref(props.openMode === "Create"); // show edit form VS show display
const model = ref(actor(props.modelValue)); // local copy

const emit = defineEmits<{
  "update:info": [element: Actor];
  "click:detail": [element: Actor, offset: number];
  "click:remove": [element: Actor];
  "click:cancel": [];
  "click:create": [element: ActorCreation];
}>();

function clickSelect(target: EventTarget | null) {
  if (!target) {
    return;
  }

  var me = target as HTMLElement;

  // Trigger a scroll into view when selected
  if (props.openMode === "Edit") {
    setTimeout(() => {
      me.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }

  while (!me.classList.contains("actr-container") && me.offsetParent) {
    me = me.offsetParent as HTMLElement;
  }

  emit("click:detail", model.value, me.offsetTop);
}

function toggleEdit() {
  editMode.value = !editMode.value;
}

function cancel(andClose: boolean = false) {
  model.value = actor(props.modelValue);
  editMode.value = false;
  if (andClose) {
    emit("click:cancel");
  }
}

function save() {
  if (editActor) {
    const patch = diff(props.modelValue, model.value);
    if (Object.keys(patch).length !== 0) {
      editActor(model.value.id, patch); // Apollo will propagate the change.
    }
  }
  editMode.value = false;
}

function onRemove() {
  emit("click:remove", model.value);
}

function onOpen() {
  router.push({ name: actorsPathName, params: { id: props.modelValue.id } });
}

function onCreate() {
  var creation = { ...model.value } as any;
  delete creation.id;
  emit("click:create", creation);
  cancel();
}

const canUseInstitutionName = computed(
  () =>
    pictogram(model.value) === "institute" || model.value.type === "Prothésiste"
);

watch(model.value, () => {
  emit("update:info", { ...model.value });
});

watch(props, () => {
  minified.value = props.minified || false;
});
</script>

<template>
  <div class="actr-container">
    <div
      class="header-container"
      :class="{ pointer: props.openMode === 'Edit' }"
      @click.stop="clickSelect($event.target)"
    >
      <div class="d-header">
        <div class="spaceme">
          <img
            v-if="pictogram(model) === 'human'"
            src="@/assets/icons/actor-bold.svg"
            class="icon"
          />
          <img
            v-else-if="pictogram(model) === 'student'"
            src="@/assets/icons/student-bold.svg"
            class="icon"
          />
          <img v-else src="@/assets/icons/building-bold.svg" class="icon" />
          <h2 class="l-name">
            {{ model.institution_name || model.last_name }}
          </h2>
          <span
            v-if="!model.institution_name"
            class="colored input-header-text"
          >
            {{ model.first_name }}
          </span>
        </div>
        <h3 v-if="model.type === 'Prothésiste'">({{ model.type }})</h3>
        <h3
          v-else-if="
            pictogram(model) === 'human' || pictogram(model) === 'practitian'
          "
        >
          {{ model.title }}
        </h3>

        <h3 v-else>({{ model.type }})</h3>
      </div>
    </div>
    <div class="content-container">
      <div class="two-cols-split">
        <div class="crow appart">
          <!-- Editable ids -->
          <div>
            <FormInput
              v-if="!editMode"
              label="Type"
              v-model="model.type"
              type="text"
              :disabled="true"
            />
            <FormSelect
              v-else
              label="Type"
              v-model="model.type"
              :options="actorTypeSelectOptions"
              :disabled="false"
            />
          </div>
          <div style="width: 120px">
            <FormInput
              label="Nb. dossiers"
              type="number"
              :model-value="model.cases.length"
              :disabled="true"
              v-if="!editMode"
            />
          </div>
        </div>
      </div>
      <template v-if="editMode">
        <div class="crow" v-if="canUseInstitutionName">
          <!-- Name -->
          <FormInput
            label="Nom de l'institution"
            type="text"
            v-model="model.institution_name"
            :disabled="!editMode"
          />
        </div>
        <div class="crow">
          <!-- Name -->
          <div>
            <FormInput
              label="Titre"
              type="text"
              v-model="model.title"
              :disabled="!editMode"
            />
          </div>
          <div>
            <FormInput
              label="Nom"
              type="text"
              v-model="model.last_name"
              :disabled="!editMode"
            />
          </div>
          <div>
            <FormInput
              label="Prénom"
              type="text"
              v-model="model.first_name"
              :disabled="!editMode"
            />
          </div>
        </div>
      </template>

      <!-- Title and name for other -->
      <div class="crow" v-if="!editMode && !minified && model.type === 'Autre'">
        <!-- Name -->
        <div>
          <FormInput
            label="Titre"
            type="text"
            v-model="model.title"
            :disabled="!editMode"
          />
        </div>
        <div>
          <FormInput
            label="Nom"
            type="text"
            v-model="model.last_name"
            :disabled="!editMode"
          />
        </div>
        <div>
          <FormInput
            label="Prénom"
            type="text"
            v-model="model.first_name"
            :disabled="!editMode"
          />
        </div>
      </div>

      <!-- Address -->
      <div class="two-cols-split" v-if="!minified">
        <!-- -->
        <div class="crow">
          <FormInput
            label="Adresse"
            type="text"
            v-model="model.contact.address"
            :disabled="!editMode"
          />
        </div>
        <div class="crow">
          <div style="width: 70px">
            <FormInput
              label=""
              type="text"
              v-model="model.contact.zipcode"
              :disabled="!editMode"
            />
          </div>
          <div>
            <FormInput
              label=""
              type="text"
              v-model="model.contact.city"
              :disabled="!editMode"
            />
          </div>
        </div>
      </div>

      <!-- Contact -->
      <div class="two-cols-split" v-if="!minified">
        <div class="crow">
          <FormInput
            label="Email"
            type="text"
            v-model="model.contact.email"
            :disabled="!editMode"
          />
        </div>
        <div class="crow">
          <div style="width: 130px">
            <FormInput
              label="Tel. Fixe"
              type="tel"
              v-model="model.contact.phone_landline"
              :disabled="!editMode"
            />
          </div>
          <div style="width: 130px">
            <FormInput
              label="Tel. Mobile"
              type="tel"
              v-model="model.contact.phone_cellular"
              :disabled="!editMode"
            />
          </div>
        </div>
      </div>

      <div class="crow" v-if="!minified">
        <MDBTextarea
          label="Notes"
          rows="2"
          v-model="model.notes"
          :disabled="!editMode"
        />
      </div>
    </div>
    <div class="content-footer" v-if="!minified">
      <div v-if="openMode == 'Edit'" class="buttons">
        <!-- Edit -->
        <span v-if="editMode" class="button" @click.stop="cancel()">{{
          "Annuler"
        }}</span>
        <span v-if="editMode" class="button" @click.stop="save()">{{
          "Enregistrer"
        }}</span>
        <span v-else class="button" @click.stop="toggleEdit()">{{
          "Editer"
        }}</span>
      </div>
      <div v-else-if="openMode == 'InlineList'" class="buttons">
        <!-- View -->
        <span v-if="!editMode" class="button" @click.stop="onRemove()">{{
          "Retirer"
        }}</span>
        <span v-else class="button" @click.stop="cancel()">{{
          "Annuler"
        }}</span>
        <span v-if="editMode" class="button" @click.stop="save()">{{
          "Enregistrer"
        }}</span>
        <span v-else class="button" @click.stop="toggleEdit()">{{
          "Editer"
        }}</span>
        <span v-if="!editMode" class="button" @click.stop="onOpen()">{{
          "Voir les dossiers"
        }}</span>
      </div>
      <div v-else-if="openMode == 'Create'" class="buttons">
        <!-- Create -->
        <span class="button" @click.stop="cancel(true)">{{ "Annuler" }}</span>
        <span class="button" @click.stop="onCreate()">{{ "Enregistrer" }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  display: inline-block;
  padding-right: 8px;
}

.entry {
  text-decoration: underline;
}

h2,
h3,
.colored {
  color: var(--vt-c-purple);
  &.no-color {
    color: inherit;
  }
}

.nowrap {
  white-space: nowrap;
}

h2.l-name,
h3.l-name {
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pointer {
  cursor: pointer;
}

.actr-container {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 680px;
  min-height: 40px;

  border-radius: 0 6px 6px 0;

  background-color: var(--color-background-soft);
  transition: all 0.1s ease-in-out;

  &.list:hover,
  &.selected {
    transition: transform 0.2s ease-in;
    transform: translateX(12px);
  }

  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;

    border-width: var(--cdo-border-thin-width);
    border-bottom-style: solid;
    border-color: var(--vt-c-purple);
    white-space: nowrap;

    .d-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      width: 100%;
      margin-left: 1em;
      margin-right: 1em;
    }

    .spaceme {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;

      margin-right: 1em;
      flex-wrap: wrap;
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1.5em 1em 1em 1em;
    row-gap: 1.3em;

    .two-cols-split {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-evenly;
      column-gap: 1em;
    }

    .crow {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      flex-wrap: nowrap;
      column-gap: 1em;
      width: 100%;

      &.appart {
        justify-content: space-between;
      }

      &.v-space {
        margin-top: 1em;
      }
    }
  }

  .content-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    margin-left: 1em;
    margin-bottom: 1.25em;

    .buttons {
      span {
        margin-left: 1em;
      }
    }

    div:last-child {
      margin-right: 1em;
    }
  }
}
</style>
